import { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Full-viewport overlay shown while a mutating request is in flight.
 * Blurs the page behind so the user can't interact with anything
 * mid-write, and the "no cierres" hint discourages closing the tab
 * before the round-trip finishes.
 *
 * Convention across the app: when a dialog submits, close the dialog
 * immediately and render this overlay at the page root. Same for inline
 * page buttons (Aprobaciones approve/reject, Tesorería Cierre, etc.).
 * Replaces the older in-button `<Loader2>` + `disabled={isPending}`
 * pattern, which left the dialog open and editable mid-flight.
 *
 * Pass `message` to describe the operation (e.g. "Anulando…",
 * "Subiendo factura…"). For multi-step flows, lift `message` into a
 * state cell and update it between steps so the user sees progress.
 *
 * `forwardRef` lets Radix Dialog's `SlotClone` thread a ref through
 * when this overlay is rendered as a child of a primitive that
 * forwards refs (e.g. `Dialog.Content asChild`). Without it React
 * logged "Function components cannot be given refs" on every dialog
 * that wrapped the overlay.
 */
type ProcessingOverlayProps = {
  message?: string;
};

export const ProcessingOverlay = forwardRef<HTMLDivElement, ProcessingOverlayProps>(
  function ProcessingOverlay({ message = 'Procesando…' }, ref) {
    return (
      <div
        ref={ref}
        role="status"
        aria-live="polite"
        className="fixed inset-0 z-[60] flex flex-col items-center justify-center gap-3 bg-background/60 backdrop-blur-sm"
      >
        <Loader2 className="h-10 w-10 animate-spin text-primary" aria-hidden />
        <p className="text-sm font-medium text-foreground">{message}</p>
        <p className="text-xs text-muted-foreground">
          No cierres esta ventana hasta que finalice.
        </p>
      </div>
    );
  },
);
